/**
 * Citizen → staff handoff for advisor demos.
 * Prefers server /staff-sessions; falls back to localStorage for offline / same-browser demos.
 */

import { SF_STAFF_SESSION_KEY } from '../data/sfAdvisorIntake';

const DEFAULT_KEY = SF_STAFF_SESSION_KEY || 'if-sf-advisor-staff-sessions-v1';

/** Known demo keys so staff page can list across cities without a query. */
export const KNOWN_STAFF_SESSION_KEYS = [
  DEFAULT_KEY,
  'if-sf-advisor-staff-sessions-v1',
  'if-berkeley-staff-sessions-v1',
  'if-san-jose-staff-sessions-v1',
  'if-demo-generic-staff-sessions-v1',
  'if-jurisdiction-staff-sessions-v1',
];

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function resolveKey(sessionKey) {
  const key = String(sessionKey || '').trim();
  return key || DEFAULT_KEY;
}

function buildLocalEntry(packet = {}) {
  const now = new Date().toISOString();
  const intent = packet.intent || {};
  const packageId = packet.packageId || packet.jurisdictionId || 'city';
  const id = packet.id
    || `${packageId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  return {
    id,
    updatedAt: now,
    createdAt: packet.createdAt || now,
    packageId,
    portalVariant: packet.portalVariant || 'default',
    title: packet.title
      || intent.proposedUse
      || intent.description
      || intent.address
      || 'Advisor session',
    address: intent.address || intent.location || packet.parcel?.location || null,
    proposedUse: intent.proposedUse || intent.description || null,
    landUseStatus: packet.landUse?.statusLabel || packet.landUse?.status || null,
    primaryTrack: packet.routing?.primaryTrackLabel || packet.routing?.primaryTrack || null,
    publicTrackingCode: packet.publicTrackingCode
      || packet.routing?.publicTrackingCode
      || null,
    packet: {
      schemaVersion: 'advisor-staff-session/v1',
      intent,
      parcel: packet.parcel || null,
      landUse: packet.landUse || null,
      routing: packet.routing || null,
      fees: packet.fees || null,
      requirements: packet.requirements || null,
      summaryMarkdown: packet.summaryMarkdown || null,
      projectSummary: packet.projectSummary || null,
      packageId,
      discoverySessionId: packet.discoverySessionId || packet.sessionId || null,
      publicTrackingCode: packet.publicTrackingCode
        || packet.routing?.publicTrackingCode
        || null,
      publicAppUrl: packet.publicAppUrl || packet.routing?.publicAppUrl || null,
      lnpStatus: packet.lnpStatus || packet.routing?.lnpStatus || null,
      claw: packet.claw || packet.routing?.claw || null,
      gaps: packet.gaps || null,
      intakeFields: packet.intakeFields || null,
      meta: packet.meta || null,
    },
    _storageKey: resolveKey(packet.staffSessionKey || packet.sessionKey),
  };
}

function persistLocal(entry, sessionKey) {
  if (typeof window === 'undefined') return null;
  const key = resolveKey(sessionKey || entry._storageKey);
  const prev = safeParse(localStorage.getItem(key)) || { sessions: [] };
  const sessions = Array.isArray(prev.sessions) ? prev.sessions.filter((s) => s.id !== entry.id) : [];
  sessions.unshift(entry);
  localStorage.setItem(key, JSON.stringify({
    sessions: sessions.slice(0, 25),
  }));
  return { id: entry.id, updatedAt: entry.updatedAt };
}

export function listStaffSessionsLocal(sessionKey) {
  if (typeof window === 'undefined') return [];
  if (sessionKey) {
    const data = safeParse(localStorage.getItem(resolveKey(sessionKey)));
    const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
    return sessions
      .slice()
      .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  }
  const byId = new Map();
  KNOWN_STAFF_SESSION_KEYS.forEach((key) => {
    const data = safeParse(localStorage.getItem(key));
    (Array.isArray(data?.sessions) ? data.sessions : []).forEach((s) => {
      if (s?.id && !byId.has(s.id)) byId.set(s.id, { ...s, _storageKey: key });
    });
  });
  return [...byId.values()]
    .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
}

/** Sync local-only list (legacy). Prefer listStaffSessionsAsync. */
export function listStaffSessions(sessionKey) {
  return listStaffSessionsLocal(sessionKey);
}

export function getStaffSession(id, sessionKey) {
  return listStaffSessionsLocal(sessionKey).find((s) => s.id === id) || null;
}

export function clearStaffSessions(sessionKey) {
  if (typeof window === 'undefined') return;
  if (sessionKey) {
    localStorage.removeItem(resolveKey(sessionKey));
    return;
  }
  KNOWN_STAFF_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
}

/**
 * Upsert a planner-facing session packet.
 * Writes localStorage immediately and best-effort POSTs to the server.
 * @returns {{ id: string, updatedAt: string } | null}
 */
export function saveStaffSession(packet = {}) {
  if (typeof window === 'undefined') return null;
  const entry = buildLocalEntry(packet);
  const local = persistLocal(entry, packet.staffSessionKey || packet.sessionKey);
  void saveStaffSessionRemote({ ...packet, id: undefined, localId: entry.id });
  return local;
}

/** Await server save; still mirrors to localStorage. */
export async function saveStaffSessionAsync(packet = {}) {
  const entry = buildLocalEntry(packet);
  persistLocal(entry, packet.staffSessionKey || packet.sessionKey);
  try {
    const remote = await saveStaffSessionRemote({
      ...packet,
      localId: entry.id,
      source: packet.source || 'citizen_download',
    });
    if (remote?.id) return { id: remote.id, updatedAt: remote.updatedAt || entry.updatedAt };
  } catch (_) {
    /* offline / API down — local is enough for same-browser demos */
  }
  return { id: entry.id, updatedAt: entry.updatedAt };
}

async function saveStaffSessionRemote(packet = {}) {
  const { saveStaffSessionApi } = await import('../api/applicationType.api.js');
  const data = await saveStaffSessionApi({
    intent: packet.intent,
    parcel: packet.parcel,
    landUse: packet.landUse,
    routing: packet.routing,
    fees: packet.fees,
    requirements: packet.requirements,
    summaryMarkdown: packet.summaryMarkdown,
    projectSummary: packet.projectSummary,
    title: packet.title,
    packageId: packet.packageId || packet.jurisdictionId,
    portalVariant: packet.portalVariant,
    discoverySessionId: packet.discoverySessionId || packet.sessionId,
    sessionId: packet.sessionId || packet.discoverySessionId,
    publicTrackingCode: packet.publicTrackingCode,
    publicAppUrl: packet.publicAppUrl,
    lnpStatus: packet.lnpStatus,
    applicationTypeId: packet.applicationTypeId,
    claw: packet.claw,
    gaps: packet.gaps,
    intakeFields: packet.intakeFields,
    meta: packet.meta,
    source: packet.source || 'citizen_download',
  });
  return data?.session || null;
}

/** Merge server + local sessions (server wins on id collision). */
export async function listStaffSessionsAsync({ packageId } = {}) {
  const want = packageId ? String(packageId).trim() : '';
  const local = listStaffSessionsLocal().filter((s) => (
    !want || s.packageId === want || s.packet?.packageId === want
  ));
  let remote = [];
  try {
    const { listStaffSessionsApi } = await import('../api/applicationType.api.js');
    remote = await listStaffSessionsApi({ packageId: want || undefined });
  } catch (_) {
    remote = [];
  }
  const byId = new Map();
  local.forEach((s) => {
    if (s?.id) byId.set(s.id, s);
  });
  remote.forEach((s) => {
    if (s?.id) byId.set(s.id, { ...s, _storageKey: 'server' });
  });
  return [...byId.values()]
    .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
}

export async function getStaffSessionAsync(id, sessionKey) {
  if (!id) return null;
  if (/^[a-f0-9]{24}$/i.test(String(id))) {
    try {
      const { getStaffSessionApi } = await import('../api/applicationType.api.js');
      const remote = await getStaffSessionApi(id);
      if (remote) return remote;
    } catch (_) {
      /* fall through */
    }
  }
  return getStaffSession(id, sessionKey);
}

export function importStaffSessionFromJson(jsonText, sessionKey) {
  const data = typeof jsonText === 'string' ? safeParse(jsonText) : jsonText;
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid JSON');
  }
  const packet = data.packet && data.schemaVersion?.includes('staff')
    ? data.packet
    : data;
  return saveStaffSession({
    id: data.id,
    intent: packet.intent || data.intent || {},
    parcel: packet.parcel || data.parcel,
    landUse: packet.landUse || data.landUse,
    routing: packet.routing || data.routing,
    fees: packet.fees || data.fees,
    requirements: packet.requirements || data.requirements,
    summaryMarkdown: packet.markdown || packet.summaryMarkdown || data.markdown,
    projectSummary: packet,
    title: data.title,
    packageId: packet.packageId || data.packageId,
    staffSessionKey: sessionKey || data.staffSessionKey,
    source: 'import',
  });
}
